import { MarkerType } from '@apps/main/modules/maps/markerFormatters';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { MapTypeId } from '@libs/maps';
import type { Marker } from '@libs/maps';

const { t } = i18n.global;

const LOCATION_AND_DESTINATION_MARKERS = [
  MarkerType.Location,
  MarkerType.DetailsAccommodation,
  MarkerType.Car,
  MarkerType.GeoLocation,
] as string[];

type TripSummaryMarkerType =
  | MarkerType.Location
  | MarkerType.DetailsAccommodation
  | MarkerType.Car
  | MarkerType.GeoLocation;

const MARKER_URLS = {
  GEOLOCATION: {
    WHITE:
      'https://cdn.sanity.io/images/inntox6v/production/47332d0827f9cdb298719fea22776b6747a59d1a-44x44.svg',
    DARK: 'https://cdn.sanity.io/images/inntox6v/production/9687ea609a7928b2fd44a853149df9b7cf26c50c-44x44.svg',
  },
  LOCATION: {
    WHITE:
      'https://cdn.sanity.io/images/inntox6v/production/3f3f18a9816107f31595330a1337d5079309b934-44x44.svg',
    DARK: 'https://cdn.sanity.io/images/inntox6v/production/4d1e5f99811827e70fdca3b2fa5ba3d871718819-44x44.svg',
  },
  CAR: {
    WHITE:
      'https://cdn.sanity.io/images/inntox6v/production/96f420e59734a96e3dc96d859b392628bdd3e298-44x44.svg',
    DARK: 'https://cdn.sanity.io/images/inntox6v/production/8ff9e47a784627b0a046c815cf592b1cd1f614a0-44x44.svg',
  },
  DETAILS_ACCOMMODATION: {
    WHITE:
      'https://cdn.sanity.io/images/inntox6v/production/542dfdb14801c9389bea94a1abbf7cd836404706-44x44.svg',
    DARK: 'https://cdn.sanity.io/images/inntox6v/production/2a4aafbdfb33ae617ca6f7d965283fc313b66b53-44x44.svg',
  },
};

function createMarkerElement(
  className: string,
  imageUrl: string,
  altText: string
): HTMLDivElement {
  const div = document.createElement('div');
  div.className = className;
  div.innerHTML = `<img src="${imageUrl}" width="35" height="35" alt="${altText}">`;
  return div;
}

export function getPrimaryMapMarkers(markers: Marker[]): Marker[] {
  return markers.filter(
    (marker) =>
      marker.useContentFormatter &&
      LOCATION_AND_DESTINATION_MARKERS.includes(marker.useContentFormatter)
  );
}

export function getMarkerFormatters(
  mapTypeId: MapTypeId
): Record<TripSummaryMarkerType, (marker: Marker) => HTMLDivElement> {
  return {
    [MarkerType.GeoLocation]: (marker): HTMLDivElement => {
      const url =
        mapTypeId === MapTypeId.HYBRID
          ? MARKER_URLS.GEOLOCATION.WHITE
          : MARKER_URLS.GEOLOCATION.DARK;
      const alt = `${t('a11y.current_location_marker')} ${marker.title}`;

      return createMarkerElement('trip-summary-geolocation-marker', url, alt);
    },
    [MarkerType.Location]: (marker): HTMLDivElement => {
      const url =
        mapTypeId === MapTypeId.HYBRID
          ? MARKER_URLS.LOCATION.WHITE
          : MARKER_URLS.LOCATION.DARK;
      const alt = `${t('a11y.current_location_marker')} ${marker.title}`;

      return createMarkerElement('trip-summary-location-marker', url, alt);
    },
    [MarkerType.Car]: (marker): HTMLDivElement => {
      const url =
        mapTypeId === MapTypeId.HYBRID
          ? MARKER_URLS.CAR.WHITE
          : MARKER_URLS.CAR.DARK;

      const alt = `${t('a11y.car_marker')} ${marker.title}`;

      return createMarkerElement('trip-summary-car-marker', url, alt);
    },
    [MarkerType.DetailsAccommodation]: (marker): HTMLDivElement => {
      const url =
        mapTypeId === MapTypeId.HYBRID
          ? MARKER_URLS.DETAILS_ACCOMMODATION.WHITE
          : MARKER_URLS.DETAILS_ACCOMMODATION.DARK;
      const alt = `${t('a11y.destination_location_marker')} ${marker.subtitle}`;
      return createMarkerElement(
        `trip-summary-accommodation-marker ${marker.cssClass || ''}`,
        url,
        alt
      );
    },
  };
}
